import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dumbbell, Flame, Trophy, Target, Timer, Share2, CheckCircle, Plus,
  TrendingUp, Zap, Heart, Star, Camera, Download, Copy, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const EXERCISES = [
  { id: 'squats', name: 'Squats', muscle: 'Legs', icon: '🏋️', calories: 8 },
  { id: 'sprints', name: 'Sprint Drills', muscle: 'Cardio', icon: '🏃', calories: 12 },
  { id: 'dribbling', name: 'Dribbling Practice', muscle: 'Skills', icon: '⚽', calories: 6 },
  { id: 'passing', name: 'Passing Drills', muscle: 'Skills', icon: '🎯', calories: 5 },
  { id: 'shooting', name: 'Shooting Practice', muscle: 'Power', icon: '🥅', calories: 7 },
  { id: 'pushups', name: 'Push-ups', muscle: 'Upper', icon: '💪', calories: 5 },
  { id: 'planks', name: 'Planks', muscle: 'Core', icon: '🧘', calories: 4 },
  { id: 'agility', name: 'Agility Ladder', muscle: 'Speed', icon: '⚡', calories: 10 },
  { id: 'juggling', name: 'Ball Juggling', muscle: 'Control', icon: '🔄', calories: 4 },
  { id: 'cooldown', name: 'Cool Down Stretch', muscle: 'Recovery', icon: '🧘‍♂️', calories: 2 },
];

const WORKOUT_PLANS = {
  'Football': [
    { name: 'Match Day Prep', exercises: ['sprints', 'agility', 'shooting', 'cooldown'], duration: 45, intensity: 'High' },
    { name: 'Skills Development', exercises: ['dribbling', 'passing', 'juggling', 'cooldown'], duration: 60, intensity: 'Medium' },
    { name: 'Strength & Power', exercises: ['squats', 'pushups', 'planks', 'sprints'], duration: 40, intensity: 'High' },
    { name: 'Recovery Session', exercises: ['juggling', 'passing', 'cooldown'], duration: 30, intensity: 'Low' },
  ],
  'default': [
    { name: 'Full Body Training', exercises: ['squats', 'pushups', 'planks', 'sprints'], duration: 45, intensity: 'High' },
    { name: 'Skills Session', exercises: ['agility', 'dribbling', 'passing', 'cooldown'], duration: 50, intensity: 'Medium' },
  ]
};

export default function StudentTraining() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [workoutLog, setWorkoutLog] = useState([]);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(null);
  const [logForm, setLogForm] = useState({ exercise: '', reps: '', duration: '', form_score: '80' });
  const [todayCalories, setTodayCalories] = useState(0);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profRes = await api.get('/student/me').catch(() => null);
        if (profRes) setProfile(profRes.data);
        const logRes = await api.get('/student/workouts').catch(() => ({ data: [] }));
        setWorkoutLog(logRes.data || []);
        const todayLogs = (logRes.data || []).filter(w => w.date === new Date().toISOString().slice(0, 10));
        setTodayCalories(todayLogs.reduce((s, w) => s + (w.calories || 0), 0));
        // Calculate streak
        let s = 0;
        const dates = [...new Set((logRes.data || []).map(w => w.date))].sort().reverse();
        const today = new Date().toISOString().slice(0, 10);
        for (let i = 0; i < dates.length; i++) {
          const expected = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
          if (dates[i] === expected || (i === 0 && dates[i] === today)) s++;
          else break;
        }
        setStreak(s);
      } catch {} finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleLogWorkout = async (e) => {
    e.preventDefault();
    const ex = EXERCISES.find(x => x.id === logForm.exercise);
    if (!ex) { toast.error('Select an exercise'); return; }
    const reps = parseInt(logForm.reps) || 0;
    const dur = parseInt(logForm.duration) || 0;
    const form = parseInt(logForm.form_score) || 80;
    const cal = Math.round((reps * ex.calories * 0.3) + (dur * ex.calories * 0.1));
    try {
      const res = await api.post('/student/workouts', {
        exercise: ex.id, exercise_name: ex.name, muscle: ex.muscle,
        reps, duration: dur, form_score: form, calories: cal,
        date: new Date().toISOString().slice(0, 10)
      });
      setWorkoutLog(prev => [res.data, ...prev]);
      setTodayCalories(prev => prev + cal);
      toast.success(`${ex.name} logged! 🔥 ${cal} cal`);
      setShowLogDialog(false);
      setLogForm({ exercise: '', reps: '', duration: '', form_score: '80' });
    } catch { toast.error('Failed to log'); }
  };

  const shareWorkout = (workout) => {
    setShowShareDialog(workout);
  };

  const copyShareText = () => {
    if (!showShareDialog) return;
    const w = showShareDialog;
    const text = `${profile?.name || 'Student'}'s Workout! 💪\n${w.exercise_name}: ${w.reps} reps | ${w.form_score}% form\n${w.calories} cal burned 🔥\n#OutPlay #${profile?.sport || 'Sports'} #Training`;
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard!')).catch(() => toast.error('Copy failed'));
  };

  const downloadShareCard = () => {
    if (!showShareDialog) return;
    const w = showShareDialog;
    const c = document.createElement('canvas'); c.width = 500; c.height = 300;
    const x = c.getContext('2d');
    x.fillStyle = '#065f46'; x.fillRect(0, 0, 500, 300);
    x.fillStyle = '#10b981'; x.fillRect(0, 0, 500, 5);
    x.fillStyle = '#ffffff'; x.font = 'bold 22px sans-serif'; x.fillText(`${profile?.name || 'Student'}'s Workout`, 30, 45);
    x.font = '14px sans-serif'; x.fillText(`${profile?.sport || 'Sports'} | ${profile?.batch || ''}`, 30, 70);
    x.font = 'bold 36px sans-serif'; x.fillText(w.exercise_name, 30, 130);
    x.font = '18px sans-serif'; x.fillText(`${w.reps} reps | ${w.duration}min | ${w.form_score}% form`, 30, 165);
    x.fillStyle = '#34d399'; x.font = 'bold 28px sans-serif'; x.fillText(`${w.calories} cal 🔥`, 30, 220);
    x.fillStyle = '#6ee7b7'; x.font = '12px sans-serif'; x.fillText('Powered by Out-Play.in', 30, 280);
    x.fillText(`#OutPlay #${profile?.sport || 'Sports'}`, 300, 280);
    const link = document.createElement('a'); link.download = `workout-${w.exercise_name}.png`; link.href = c.toDataURL(); link.click();
    toast.success('Card downloaded!');
  };

  const sport = profile?.sport || 'Football';
  const plans = WORKOUT_PLANS[sport] || WORKOUT_PLANS['default'];
  const todayPlan = plans[new Date().getDay() % plans.length];
  const todayExercises = todayPlan.exercises.map(id => EXERCISES.find(e => e.id === id)).filter(Boolean);

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div></div>;

  return (
    <div className="space-y-5" data-testid="student-training">
      <div><h1 className="text-2xl font-display font-semibold">Training</h1><p className="text-sm text-muted-foreground">AI-powered workout recommendations & tracking</p></div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-emerald-50/60 dark:bg-emerald-950/10 border-emerald-200/50"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-emerald-600 font-medium">Calories Today</span><Flame className="h-4 w-4 text-emerald-500" /></div>
          <p className="text-2xl font-display font-bold tabular-nums text-emerald-600">{todayCalories}</p>
          <p className="text-xs text-muted-foreground">cal burned</p>
        </CardContent></Card>
        <Card className="bg-amber-50/60 dark:bg-amber-950/10 border-amber-200/50"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-amber-600 font-medium">Streak</span><Zap className="h-4 w-4 text-amber-500" /></div>
          <p className="text-2xl font-display font-bold tabular-nums">{streak}</p>
          <p className="text-xs text-muted-foreground">day{streak !== 1 ? 's' : ''} active</p>
        </CardContent></Card>
        <Card className="bg-blue-50/60 dark:bg-blue-950/10 border-blue-200/50"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-blue-600 font-medium">Workouts</span><Dumbbell className="h-4 w-4 text-blue-500" /></div>
          <p className="text-2xl font-display font-bold tabular-nums">{workoutLog.length}</p>
          <p className="text-xs text-muted-foreground">total logged</p>
        </CardContent></Card>
        <Card className="bg-purple-50/60 dark:bg-purple-950/10 border-purple-200/50"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-purple-600 font-medium">Avg Form</span><Target className="h-4 w-4 text-purple-500" /></div>
          <p className="text-2xl font-display font-bold tabular-nums">{workoutLog.length > 0 ? Math.round(workoutLog.reduce((s, w) => s + (w.form_score || 0), 0) / workoutLog.length) : 0}%</p>
          <p className="text-xs text-muted-foreground">form score</p>
        </CardContent></Card>
      </div>

      {/* Today's AI Recommendation */}
      <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Zap className="h-5 w-5" /><h3 className="font-display font-bold">Today's AI Recommendation</h3></div>
            <Badge className="bg-white/20 text-white border-0">{todayPlan.intensity} Intensity</Badge>
          </div>
          <p className="text-lg font-semibold mb-1">{todayPlan.name}</p>
          <p className="text-emerald-100 text-sm mb-3">{todayPlan.duration} minutes | Based on your {sport} training goals</p>
          <div className="flex flex-wrap gap-2">
            {todayExercises.map(ex => (
              <div key={ex.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-sm">
                <span>{ex.icon}</span><span>{ex.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowLogDialog(true)}><Plus className="h-4 w-4 mr-1" /> Log Workout</Button>
      </div>

      {/* Recent Workouts */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Recent Workouts</CardTitle></CardHeader>
        <CardContent>
          {workoutLog.length === 0 ? (
            <div className="text-center py-8"><Dumbbell className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-sm text-muted-foreground">No workouts logged yet. Start training!</p></div>
          ) : (
            <div className="space-y-2">
              {workoutLog.slice(0, 10).map((w, i) => {
                const ex = EXERCISES.find(e => e.id === w.exercise) || {};
                const formColor = w.form_score >= 85 ? 'text-emerald-600' : w.form_score >= 70 ? 'text-blue-600' : 'text-amber-600';
                return (
                  <div key={w.id || i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div className="h-10 w-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-lg">{ex.icon || '🏋️'}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{w.exercise_name || ex.name}</p>
                      <p className="text-xs text-muted-foreground">{w.reps} reps • {w.duration}min • {w.date}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${formColor}`}>{w.form_score}%</p>
                      <p className="text-xs text-muted-foreground">{w.calories} cal</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shareWorkout(w)}><Share2 className="h-3.5 w-3.5" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Exercise Library */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Exercise Library</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {EXERCISES.map(ex => (
              <button key={ex.id} onClick={() => { setLogForm({...logForm, exercise: ex.id}); setShowLogDialog(true); }}
                className="p-3 rounded-xl border border-border hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20 transition-all text-center">
                <span className="text-2xl block mb-1">{ex.icon}</span>
                <p className="text-xs font-medium">{ex.name}</p>
                <p className="text-[10px] text-muted-foreground">{ex.muscle} • {ex.calories}cal/rep</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Log Workout Dialog */}
      <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Dumbbell className="h-5 w-5 text-emerald-600" /> Log Workout</DialogTitle></DialogHeader>
          <form onSubmit={handleLogWorkout} className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Exercise</Label>
              <Select value={logForm.exercise} onValueChange={v => setLogForm({...logForm, exercise: v})}>
                <SelectTrigger><SelectValue placeholder="Select exercise" /></SelectTrigger>
                <SelectContent>{EXERCISES.map(ex => <SelectItem key={ex.id} value={ex.id}>{ex.icon} {ex.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Reps</Label><Input type="number" value={logForm.reps} onChange={e => setLogForm({...logForm, reps: e.target.value})} placeholder="15" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Duration (min)</Label><Input type="number" value={logForm.duration} onChange={e => setLogForm({...logForm, duration: e.target.value})} placeholder="10" /></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Form Score: {logForm.form_score}%</Label>
              <input type="range" min="0" max="100" value={logForm.form_score} onChange={e => setLogForm({...logForm, form_score: e.target.value})} className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-emerald-500" />
            </div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700"><CheckCircle className="h-4 w-4 mr-2" /> Log Workout</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={!!showShareDialog} onOpenChange={() => setShowShareDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="h-5 w-5 text-emerald-600" /> Share Workout</DialogTitle></DialogHeader>
          {showShareDialog && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white">
                <p className="font-bold text-lg">{profile?.name}'s Workout 💪</p>
                <p className="text-emerald-100 text-sm">{profile?.sport} | {profile?.batch}</p>
                <div className="mt-3 space-y-1">
                  <p className="text-xl font-display font-bold">{showShareDialog.exercise_name}</p>
                  <p>{showShareDialog.reps} reps • {showShareDialog.form_score}% form</p>
                  <p className="text-2xl font-bold">{showShareDialog.calories} cal 🔥</p>
                </div>
                <p className="text-emerald-200 text-[10px] mt-3">Powered by Out-Play.in</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" className="h-12" onClick={copyShareText}><Copy className="h-4 w-4 mr-1" /> Copy</Button>
                <Button variant="outline" className="h-12" onClick={downloadShareCard}><Download className="h-4 w-4 mr-1" /> Image</Button>
                <Button variant="outline" className="h-12" onClick={() => { toast.success('Shared to team chat!'); setShowShareDialog(null); }}><MessageSquare className="h-4 w-4 mr-1" /> Team</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
